import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { PenaltyScheduleFormRule, PenaltyScheduleFormSchema } from "@core/form/form.rule";
import { useFormDefaultPenaltySchedule } from "@core/form/form-default-penalty-schedule.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdatePenaltySchedule, 
  useQyGetPenaltyScheduleById, 
  useQyDeletePenaltySchedule 
} from "@core/query/penalty-schedule.query";
import { GetResponsePenaltyScheduleModel } from "@core/model/query-model-penalty-schedule";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditPenaltySchedule() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultPenaltySchedule();
  const navigate = useNavigate();
  const { mapUpdatePenaltySchedule, mapDeletePenaltySchedule } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { penaltyScheduleId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID PenaltySchedule
  const handleGetApiSuccess = (data: GetResponsePenaltyScheduleModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: penaltyScheduleResponse,
    isLoading,
    isError: penaltyScheduleError,
  } = useQyGetPenaltyScheduleById(penaltyScheduleId || "", handleGetApiSuccess);
  const penaltyScheduleData = penaltyScheduleResponse?.data?.[0];

  // API UPDATE PenaltySchedule
  const handleUpdateApiSuccess = () => {
    showSuccess("PenaltySchedule updated successfully");
  };
  const { mutate: updatePenaltySchedule, isLoading: isEditLoading } = useQyUpdatePenaltySchedule(
    handleUpdateApiSuccess
  );

  // API DELETE PenaltySchedule
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("PenaltySchedule deleted successfully");
  };
  const { mutate: deletePenaltySchedule } = useQyDeletePenaltySchedule(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PenaltyScheduleFormSchema>({
    defaultValues: getDefault(penaltyScheduleData),
    resolver: zodResolver(PenaltyScheduleFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PenaltyScheduleFormSchema) => {
    if (!penaltyScheduleData) {
      throw new Error("No penaltySchedule data");
    }

    const formData = mapUpdatePenaltySchedule(form, penaltyScheduleData.code);
    updatePenaltySchedule(formData);
  };
  const handleValidateError = (err: FieldErrors<PenaltyScheduleFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!penaltyScheduleData) {
      throw new Error("No penaltySchedule data found");
    }

    const form = mapDeletePenaltySchedule(penaltyScheduleData.code);
    deletePenaltySchedule(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    penaltyScheduleId,
    penaltyScheduleData,
    isLoading,
    penaltyScheduleError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}